import React from 'react';
import { useLocation, Link, Navigate } from 'react-router-dom';
import { CheckCircle, Download, Printer, ChevronRight } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
const screenOrders: Record<string, any> = {
  paid: {
    orderId: 'ORD-482913',
    amount: 72999,
    paymentMethod: 'Credit/Debit Card',
    paymentStatus: 'Paid',
    receiptNo: 'REC-58201934',
    date: new Date().toISOString(),
    cardLast4: '8037'
  },
  cod: {
    orderId: 'ORD-517204',
    amount: 72999,
    paymentMethod: 'Cash on Delivery',
    paymentStatus: 'Pending'
  }
};
export function OrderConfirmationPage() {
  const location = useLocation();
  const screenInit = useScreenInit() as {
    order?: string;
  };
  const orderData = location.state || (screenInit.order ? screenOrders[screenInit.order] : null);
  // If accessed directly without order data, redirect to home
  if (!orderData) {
    return <Navigate to="/" replace data-id="element-1417" data-mp="2dca610b8wb7b" />;
  }
  const {
    orderId,
    amount,
    paymentMethod,
    paymentStatus,
    receiptNo,
    date,
    cardLast4
  } = orderData;
  const isPaid = paymentStatus === 'Paid';
  return <div className="max-w-3xl mx-auto py-12 px-4 sm:px-6 lg:px-8" data-id="element-1418" data-mp="3dp2k4copbx38">
      <div className="text-center mb-10" data-id="element-1419" data-mp="2zoi0xfevjz97">
        <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" data-id="element-1420" data-mp="2zpjb7ho2ghpv" />
        <h1 className="text-3xl font-extrabold text-gray-900" data-id="element-1421" data-mp="33qd3socgekp3">
          Order Confirmed!
        </h1>
        <p className="mt-2 text-lg text-gray-600" data-id="element-1422" data-mp="3rg7d4vxkdr4l">
          Thank you for your purchase. Your order{' '}
          <span className="font-semibold text-gray-900" data-id="element-1423" data-mp="839bvl2aqaj">{orderId}</span> has
          been received.
        </p>
      </div>

      {isPaid && <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200 mb-8" data-id="element-1424" data-mp="3ikrl8kgddm1n">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1425" data-mp="3mltfsnzcd9xa">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1426" data-mp="iz2v6nx9fjzl">
              Payment Receipt
            </h2>
            <div className="flex space-x-2" data-id="element-1427" data-mp="svl3qj9j20kk">
              <button className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors" title="Print Receipt" data-id="element-1428" data-mp="sujixn2plzpn">
                <Printer className="h-5 w-5" data-id="element-1429" data-mp="ju4s686z7ser" />
              </button>
              <button className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors" title="Download PDF" data-id="element-1430" data-mp="jwhknfad1l62">
                <Download className="h-5 w-5" data-id="element-1431" data-mp="9wqqjeppp86i" />
              </button>
            </div>
          </div>

          <div className="p-6 sm:p-8" data-id="element-1432" data-mp="558muiwb0thu">
            <div className="flex justify-between items-start mb-8" data-id="element-1433" data-mp="547amb3jy0yj">
              <div className="flex items-center gap-3" data-id="element-1434" data-mp="14yj7cvzif65">
                <img src="https://cdn.magicpatterns.com/uploads/6qgM4mDpnqGm6ucRQC8GRZ/Gadgets_Zone_Logo.png" alt="Gadgets Zone" className="h-10 object-contain" data-id="element-1435" data-mp="3sa6n4jd2wh94" />
                <div data-id="element-1436" data-mp="3nalpcc9naexz">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1437" data-mp="3nd7xoe4d0yh9">
                    Gadgets Zone
                  </h3>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-1438" data-mp="388c3oqyry5uf">
                    contact@gadgetszone.com
                  </p>
                </div>
              </div>
              <div className="text-right" data-id="element-1439" data-mp="3d99l31bymvuv">
                <p className="text-sm font-medium text-gray-500" data-id="element-1440" data-mp="3i5qadyuwbg9j">Receipt No.</p>
                <p className="text-base font-semibold text-gray-900" data-id="element-1441" data-mp="3n5cjbw3oyacj">
                  {receiptNo}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-y-6 gap-x-4 mb-8" data-id="element-1442" data-mp="2pj5jezic166i">
              <div data-id="element-1443" data-mp="2ufvybjiehvqc">
                <p className="text-sm font-medium text-gray-500" data-id="element-1444" data-mp="2uhgsh80szcno">Date & Time</p>
                <p className="text-base text-gray-900" data-id="element-1445" data-mp="2zfi79gr74ptc">
                  {new Date(date).toLocaleString()}
                </p>
              </div>
              <div data-id="element-1446" data-mp="348l7g7qhaxii">
                <p className="text-sm font-medium text-gray-500" data-id="element-1447" data-mp="26r4af4bufzjd">Order ID</p>
                <p className="text-base text-gray-900" data-id="element-1448" data-mp="26p082qtlf8l7">{orderId}</p>
              </div>
              <div data-id="element-1449" data-mp="2bocw3ew7alpd">
                <p className="text-sm font-medium text-gray-500" data-id="element-1450" data-mp="2bnurnuzdx16d">
                  Payment Method
                </p>
                <p className="text-base text-gray-900 flex items-center" data-id="element-1451" data-mp="cb5y2q8e5a1m">
                  {paymentMethod} {cardLast4 && `ending in ${cardLast4}`}
                </p>
              </div>
              <div data-id="element-1452" data-mp="m925p824dmuz">
                <p className="text-sm font-medium text-gray-500" data-id="element-1453" data-mp="m8jzpjpnnu9v">Status</p>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1" data-id="element-1454" data-mp="100gp3fzxvq2k">
                  {paymentStatus}
                </span>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6" data-id="element-1455" data-mp="19ywijpmfvbf8">
              <div className="flex justify-between items-center text-lg font-bold" data-id="element-1456" data-mp="19wa7vh494fss">
                <span className="text-gray-900" data-id="element-1457" data-mp="14xgudooysosm">Amount Paid</span>
                <span className="text-blue-600" data-id="element-1458" data-mp="3bqss4tp1qb7d">
                  Rs. {amount.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200" data-id="element-1459" data-mp="1m8celd5wckjw">
            <div className="flex items-center justify-center text-xs text-gray-500" data-id="element-1460" data-mp="1r7z2qhn9jl7h">
              <span data-id="element-1461" data-mp="1ra1non9k6o5c">Transaction processed securely by</span>
              <img src="https://cdn.magicpatterns.com/uploads/wtigZ1ySqavxJHCJbfDDio/Paysys_Logo.png" alt="Paysys Labs" className="h-4 object-contain ml-2" data-id="element-1462" data-mp="1ci15zdb9pn6o" />
            </div>
          </div>
        </div>}

      {!isPaid && <div className="bg-white shadow rounded-lg p-6 border border-gray-200 mb-8" data-id="element-1463" data-mp="13gtcr9nlzcjg">
          <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1464" data-mp="18i8xf0lvjh1b">
            Order Details
          </h2>
          <div className="space-y-3" data-id="element-1465" data-mp="tlrmublie6ft">
            <div className="flex justify-between" data-id="element-1466" data-mp="yjbbm5fu21m0">
              <span className="text-gray-500" data-id="element-1467" data-mp="yk2xupnjimzm">Payment Method</span>
              <span className="font-medium" data-id="element-1468" data-mp="2nxpx8gvk18da">{paymentMethod}</span>
            </div>
            <div className="flex justify-between" data-id="element-1469" data-mp="2sxc7chg368hv">
              <span className="text-gray-500" data-id="element-1470" data-mp="2sw13khmn50qr">Total Amount</span>
              <span className="font-medium" data-id="element-1471" data-mp="2rgoq43in6y98">Rs. {amount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between" data-id="element-1472" data-mp="2mgj699im6g1p">
              <span className="text-gray-500" data-id="element-1473" data-mp="2mfr93u9dca9b">Status</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-1474" data-mp="2hh7n5turyygy">
                {paymentStatus}
              </span>
            </div>
          </div>
        </div>}

      <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1475" data-mp="3a5vbjyatm3yq">
        <Link to="/" className="inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors" data-id="element-1476" data-mp="358ld5t5gehyr">
          Continue Shopping
        </Link>
        <Link to="/account" className="inline-flex justify-center items-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1477" data-mp="2wd5m1jw32gwv">
          View Order History
        </Link>
      </div>
    </div>;
}